import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Col } from 'reactstrap';
import {
    Button,
    JustifyContents,
    Alignment,
    Icon,
    SearchInput,
    Variant,
    SelectInput,
    TsrSvgIcon,
    TsrTooltip,
    TooltipType,
    InputWidth,
    DropButton,
} from 'tacho-ui';
import { TachoReleaseInformationModel } from '../../../../core/domainModels/versions/TachoInstallerModel';
import { ApplicationState } from '../../../../state/ApplicationState';
import { TachoToolbar, TachoToolbarHorizontalLine } from '../../../components/TachoToolbar';
import { defaultAPIDateFormat } from '../../../../core/helpers/moment';
import config from '../../../../config';

interface VersionsToolbarProps {
    selectedVersions: Array<TachoReleaseInformationModel>;
    selectedLicenceId?: string;
    selectedLicenceVersionId?: string;
    selectAllLicences: string;
    getInstallersForLicenceError?: string;
    setSelectedLicence: (licence: string) => void;
    openConfirmRemoveModal: () => void;
    deletePending?: boolean;
    openAddInstallerModal: () => void;
    openEditInstallerModal: () => void;
    onRefresh: () => void;
    onSearchQueryChanged: (newQuery: string) => void;
    setSelectedVersion: (version: string) => void;
}
export const VersionsToolbar = (props: VersionsToolbarProps) => {
    const { formatMessage } = useIntl();
    const [typingTimeout, setTypingTimeout] = useState(0);
    const searchQueryTypingTimeoutMS = 500;
    const licences = useSelector((state: ApplicationState) => state.licences.licences);
    function onSearchInputChanged(newValue: string): void {
        setTypingTimeout(Date.now() + searchQueryTypingTimeoutMS);
        const timeoutDelayMS = 10;
        setTimeout(() => {
            const now = Date.now();
            if (now > typingTimeout) {
                props.onSearchQueryChanged(newValue);
            }
        }, searchQueryTypingTimeoutMS + timeoutDelayMS);
    }
    const licencesOptions = useMemo(() => {
        let licencesArray = licences.map(item => item.friendlyLicenseNumber.toString());
        licencesArray.unshift(props.selectAllLicences);
        return licencesArray;
    }, [licences]);

    const versionOptions = useMemo(() => {
        let licencesArray = licences
            .find(item => item.friendlyLicenseNumber.toString() === props.selectedLicenceId)
            ?.licenseVersions.map(
                item => `${item.validFrom.toFormat(defaultAPIDateFormat)} - ${item.validTo.toFormat(defaultAPIDateFormat)}`,
            );
        return licencesArray;
    }, [licences, props.selectedLicenceId]);

    const downloadOptions = useMemo(
        () => [
            {
                label: formatMessage({ id: 'versionsNoSqlVersion' }),
                onItemClick: () => {
                    window.location.href = config.tachoAPI.url + '/TachoReleasesInformation/LatestInstaller?variant=nosql';
                },
                iconName: Icon.Download,
            },
            {
                label: formatMessage({ id: 'versionsFullVersion' }),
                onItemClick: () => {
                    window.location.href = config.tachoAPI.url + '/TachoReleasesInformation/LatestInstaller';
                },
                iconName: Icon.Download,
            },
        ],
        [],
    );

    return (
        <TachoToolbar>
            <Col className="d-flex flex-wrap gap-2">
                <Button
                    theme="primary"
                    text={formatMessage({ id: 'commonAdd' })}
                    onClick={props.openAddInstallerModal}
                    icon={Icon.Add}
                />
                <Button
                    theme="primary"
                    text={formatMessage({ id: 'commonEdit' })}
                    onClick={props.openEditInstallerModal}
                    icon={Icon.Edit}
                    disabled={props.selectedVersions.length !== 1}
                />
                <Button
                    justify={JustifyContents.center}
                    align={Alignment.center}
                    gap={4}
                    text={formatMessage({ id: 'commonDelete' })}
                    icon={Icon.Delete}
                    className="primary"
                    onClick={props.openConfirmRemoveModal}
                    disabled={props.selectedVersions.length === 0 || !!props.deletePending}
                />
                <TachoToolbarHorizontalLine />
                <SelectInput
                    label={formatMessage({ id: 'versionsLicenceFilter' })}
                    value={props.selectedLicenceId || ''}
                    onChange={value => {
                        props.setSelectedLicence(value as string);
                        props.setSelectedVersion('');
                    }}
                    options={licencesOptions}
                    variant={Variant.Toolbar}
                    width={InputWidth.ToolbarSelect}
                    gap={5}
                    labelLength={-1}
                />
                <SelectInput
                    label={formatMessage({ id: 'versionsLicenceVersionFilter' })}
                    value={props.selectedLicenceVersionId || ''}
                    onChange={value => {
                        props.setSelectedVersion(value as string);
                    }}
                    options={versionOptions}
                    disabled={props.selectedLicenceId === props.selectAllLicences}
                    variant={Variant.Toolbar}
                    width={InputWidth.ToolbarSelectXL}
                    gap={5}
                    labelLength={-1}
                />
                <SearchInput
                    onChange={onSearchInputChanged}
                    width="auto"
                    height="auto"
                    variant={Variant.Toolbar}
                    placeholder={formatMessage({ id: 'commonSearch' })}
                    value={undefined}
                />
                <Button
                    justify={JustifyContents.center}
                    align={Alignment.center}
                    gap={4}
                    icon={Icon.Refresh}
                    className="primary"
                    onClick={props.onRefresh}
                />
                <DropButton
                    array={downloadOptions}
                    title={formatMessage({ id: 'versionsDownloadLatestVersion' })}
                    icon={Icon.Download}
                    className="primary"
                    loading={false}
                    disabled={false}
                />
                {props.getInstallersForLicenceError && (
                    <TsrTooltip icon type={TooltipType.Error} content={formatMessage({ id: props.getInstallersForLicenceError })}>
                        <div className="d-flex align-items-center">
                            <TsrSvgIcon titleIcon icon="information" color="error" size="large" pointer />
                        </div>
                    </TsrTooltip>
                )}
            </Col>
        </TachoToolbar>
    );
};
